import eventBus from 'common/event-handling/event-bus.model';
import { PreloadedEvent } from 'common/event-handling/events';
import { sendCustomTrack } from 'common/js/TrackAnalyticsHelper';

export class DcmBase64IframeDetector {
  static DCM_ADS_CLASS = 'dcmads';

  private static DCM_GOOGLE_ADS_CLASS = 'GoogleActiveViewElement';
  private static DCM_INTERMEDIARY_IFRAME_TITLE = 'advertisement';

  private redirectBaseIframe: HTMLIFrameElement | null | undefined;

  public checkForBase64Iframe(): void {
    const subscriptionId = eventBus.subscribe(PreloadedEvent, async _ => {
      eventBus.unsubscribe(subscriptionId);

      const dcmFrame = this.getDcmRootDiv();

      if (!dcmFrame) {
        return;
      }

      this.watchForChange(dcmFrame);
    });
  }

  private watchForChange(element: HTMLElement): void {
    // Initial check to avoid MutationObserver if the final frame is already present
    if (this.checkForBase64Src()) {
      return;
    }

    // The final frame might not be loaded yet, so we observe mutations on the parent element and check for base64 src when a new frame is detected
    const observer = new MutationObserver(mutationsList =>
      this.mutationObserverCallback(mutationsList, observer)
    );

    observer.observe(element, {
      childList: true,
      subtree: true
    });
  }

  private mutationObserverCallback(
    mutationsList: MutationRecord[],
    observer: MutationObserver
  ): void {
    mutationsList.forEach(mutation => {
      if (mutation.type !== 'childList') {
        return;
      }

      mutation.addedNodes.forEach(addedNode => {
        if (addedNode.nodeName === 'IFRAME') {
          const addedIFrame = addedNode as HTMLIFrameElement;

          if (this.isIntermediaryIFrame(addedIFrame)) {
            // Sometime, DCM creates an intermediary iframe before the final one
            // we need to deep dive into it
            observer.disconnect();

            addedIFrame.onload = () => {
              const insideDiv = (addedIFrame.contentWindow?.document.querySelector(
                `.${DcmBase64IframeDetector.DCM_GOOGLE_ADS_CLASS}`
              ) as unknown) as HTMLDivElement;
              const insideIFrame = (addedIFrame.contentWindow?.document.querySelector(
                `.${DcmBase64IframeDetector.DCM_GOOGLE_ADS_CLASS} iframe`
              ) as unknown) as HTMLIFrameElement;

              if (insideIFrame && this.checkForBase64Src(insideIFrame)) {
                return;
              }

              this.watchForChange(insideDiv);
            };
          } else if (this.checkForBase64Src(addedIFrame)) {
            observer.disconnect();
          }
        }
      });
    });
  }

  private getDcmRootDiv(): HTMLDivElement {
    this.redirectBaseIframe = document.querySelector('iframe');
    return (this.redirectBaseIframe?.contentWindow?.document.querySelector(
      `.${DcmBase64IframeDetector.DCM_ADS_CLASS}`
    ) as unknown) as HTMLDivElement;
  }

  private isIntermediaryIFrame(frame: HTMLIFrameElement): boolean {
    return (
      frame.getAttribute('title') ===
      DcmBase64IframeDetector.DCM_INTERMEDIARY_IFRAME_TITLE
    );
  }

  private checkForBase64Src(frame?: HTMLIFrameElement): boolean {
    let dcmFrame = frame;

    if (!dcmFrame) {
      dcmFrame = (this.redirectBaseIframe?.contentWindow?.document.querySelector(
        `.${DcmBase64IframeDetector.DCM_ADS_CLASS} iframe`
      ) as unknown) as HTMLIFrameElement;
    }

    const dcmFrameSrc = dcmFrame?.getAttribute('src') ?? '';
    const srcStartsWithData = dcmFrameSrc.indexOf('data:') === 0;
    const srcContainsBase64 = dcmFrameSrc.indexOf('base64') > -1;

    if (srcStartsWithData && srcContainsBase64) {
      sendCustomTrack('base64_iframe');
      return true;
    }
    return false;
  }
}
